import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Car, Plus, Search, RefreshCw, Edit, Trash2, Shield, Wrench, ClipboardCheck, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const emptyAutomezzo = { targa: '', marca: '', modello: '', anno_immatricolazione: '', tipo_veicolo: '', alimentazione: '', km_attuali: '', note: '' };
const TIPI_VEICOLO = ['Auto', 'Furgone', 'Fuoristrada', 'Ambulanza', 'Mezzo Speciale', 'Motoveicolo', 'Rimorchio', 'Altro'];
const ALIMENTAZIONI = ['Benzina', 'Diesel', 'GPL', 'Metano', 'Elettrico', 'Ibrido'];

export default function Automezzi() {
  const [automezzi, setAutomezzi] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showDialog, setShowDialog] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({...emptyAutomezzo});
  const [submitting, setSubmitting] = useState(false);
  // Dettaglio
  const [selectedAuto, setSelectedAuto] = useState(null);
  const [dettaglio, setDettaglio] = useState(null);
  const [showDettaglio, setShowDettaglio] = useState(false);
  // Sub-forms
  const [showAssicForm, setShowAssicForm] = useState(false);
  const [showRevForm, setShowRevForm] = useState(false);
  const [showManutForm, setShowManutForm] = useState(false);
  const [subFormData, setSubFormData] = useState({});

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return token ? { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' } : {};
  };

  const fetchAutomezzi = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      const res = await fetch(`${API_URL}/api/automezzi?${params}`, { headers: getAuthHeaders(), credentials: 'include' });
      if (res.ok) { const d = await res.json(); setAutomezzi(d.automezzi || []); }
    } catch (error) { console.error(error); }
    finally { setLoading(false); }
  }, [search]);

  useEffect(() => { fetchAutomezzi(); }, [fetchAutomezzi]);

  const fetchDettaglio = async (automezzo_id) => {
    const res = await fetch(`${API_URL}/api/automezzi/${automezzo_id}`, { headers: getAuthHeaders(), credentials: 'include' });
    if (res.ok) { const d = await res.json(); setDettaglio(d); setShowDettaglio(true); }
  };

  const handleSave = async () => {
    if (!formData.targa) { toast.error('Inserisci la targa'); return; }
    setSubmitting(true);
    try {
      const url = editingId ? `${API_URL}/api/automezzi/${editingId}` : `${API_URL}/api/automezzi`;
      const method = editingId ? 'PUT' : 'POST';
      const payload = {...formData, anno_immatricolazione: formData.anno_immatricolazione ? parseInt(formData.anno_immatricolazione) : null, km_attuali: formData.km_attuali ? parseInt(formData.km_attuali) : null};
      const res = await fetch(url, { method, headers: getAuthHeaders(), credentials: 'include', body: JSON.stringify(payload) });
      if (res.ok) { toast.success(editingId ? 'Automezzo aggiornato' : 'Automezzo registrato'); setShowDialog(false); fetchAutomezzi(); }
      else { const err = await res.json(); toast.error(err.detail || 'Errore'); }
    } catch { toast.error('Errore di connessione'); }
    finally { setSubmitting(false); }
  };

  const handleDelete = async (id, targa) => {
    if (!window.confirm(`Eliminare il veicolo ${targa}?`)) return;
    const res = await fetch(`${API_URL}/api/automezzi/${id}`, { method: 'DELETE', headers: getAuthHeaders(), credentials: 'include' });
    if (res.ok) { toast.success('Automezzo eliminato'); fetchAutomezzi(); }
    else { const err = await res.json(); toast.error(err.detail || 'Errore'); }
  };

  const handleSubFormSave = async (tipo) => {
    if (!selectedAuto) return;
    const endpoints = { assicurazione: 'assicurazioni', revisione: 'revisioni', manutenzione: 'manutenzioni' };
    try {
      const res = await fetch(`${API_URL}/api/automezzi/${selectedAuto.automezzo_id}/${endpoints[tipo]}`, {
        method: 'POST', headers: getAuthHeaders(), credentials: 'include', body: JSON.stringify(subFormData)
      });
      if (res.ok) {
        toast.success(`${tipo.charAt(0).toUpperCase() + tipo.slice(1)} registrata`);
        setShowAssicForm(false); setShowRevForm(false); setShowManutForm(false);
        setSubFormData({});
        fetchDettaglio(selectedAuto.automezzo_id);
      } else { const err = await res.json(); toast.error(err.detail || 'Errore'); }
    } catch { toast.error('Errore di connessione'); }
  };

  const isScaduto = (data) => { if (!data) return false; return new Date(data) < new Date(); };
  const isInScadenza = (data) => { if (!data) return false; const d = new Date(data); const now = new Date(); const diff = (d - now) / (1000*60*60*24); return diff > 0 && diff <= 30; };
  const formatDate = (d) => d ? new Date(d).toLocaleDateString('it-IT') : '-';

  return (
    <div className="space-y-6" data-testid="automezzi-page">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <Car className="w-6 h-6 text-indigo-600" /> Automezzi
          </h1>
          <p className="text-sm text-slate-500 mt-1">Gestione veicoli, assicurazioni, revisioni e manutenzioni</p>
        </div>
        <Button onClick={() => { setEditingId(null); setFormData({...emptyAutomezzo}); setShowDialog(true); }} data-testid="nuovo-automezzo-btn">
          <Plus className="w-4 h-4 mr-2" /> Nuovo Automezzo
        </Button>
      </div>

      {/* Filtro */}
      <Card>
        <CardContent className="p-4">
          <div className="flex gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input placeholder="Cerca per targa, marca, modello..." className="pl-10" value={search}
                onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && fetchAutomezzi()} data-testid="search-input" />
            </div>
            <Button variant="outline" onClick={fetchAutomezzi}><RefreshCw className="w-4 h-4" /></Button>
          </div>
        </CardContent>
      </Card>

      {/* Lista */}
      <Card>
        <CardContent className="p-0">
          {loading ? <div className="p-8 text-center text-gray-500">Caricamento...</div> :
          automezzi.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <Car className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p className="font-medium">Nessun automezzo registrato</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Targa</TableHead>
                  <TableHead>Veicolo</TableHead>
                  <TableHead className="hidden md:table-cell">Tipo</TableHead>
                  <TableHead className="hidden md:table-cell">Km</TableHead>
                  <TableHead>Assicurazione</TableHead>
                  <TableHead>Revisione</TableHead>
                  <TableHead className="text-right">Azioni</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {automezzi.map((auto) => (
                  <TableRow key={auto.automezzo_id} className="cursor-pointer hover:bg-slate-50"
                    onClick={() => { setSelectedAuto(auto); fetchDettaglio(auto.automezzo_id); }}
                    data-testid={`auto-row-${auto.automezzo_id}`}>
                    <TableCell className="font-bold font-mono">{auto.targa}</TableCell>
                    <TableCell>{auto.marca} {auto.modello}</TableCell>
                    <TableCell className="hidden md:table-cell">{auto.tipo_veicolo || '-'}</TableCell>
                    <TableCell className="hidden md:table-cell">{auto.km_attuali ? `${auto.km_attuali.toLocaleString()} km` : '-'}</TableCell>
                    <TableCell>
                      {auto.assicurazione_scadenza ? (
                        isScaduto(auto.assicurazione_scadenza) ? <Badge className="bg-red-100 text-red-700">Scaduta</Badge> :
                        isInScadenza(auto.assicurazione_scadenza) ? <Badge className="bg-amber-100 text-amber-700">In scadenza</Badge> :
                        <Badge className="bg-green-100 text-green-700">{formatDate(auto.assicurazione_scadenza)}</Badge>
                      ) : <Badge variant="outline">N/D</Badge>}
                    </TableCell>
                    <TableCell>
                      {auto.revisione_prossima ? (
                        isScaduto(auto.revisione_prossima) ? <Badge className="bg-red-100 text-red-700">Scaduta</Badge> :
                        isInScadenza(auto.revisione_prossima) ? <Badge className="bg-amber-100 text-amber-700">In scadenza</Badge> :
                        <Badge className="bg-green-100 text-green-700">{formatDate(auto.revisione_prossima)}</Badge>
                      ) : <Badge variant="outline">N/D</Badge>}
                    </TableCell>
                    <TableCell className="text-right space-x-1" onClick={(e) => e.stopPropagation()}>
                      <Button size="sm" variant="outline" onClick={() => { setSelectedAuto(auto); fetchDettaglio(auto.automezzo_id); }}
                        data-testid={`gestisci-${auto.automezzo_id}`}>
                        <Wrench className="h-4 w-4 mr-1" /> Gestisci
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => { setEditingId(auto.automezzo_id); setFormData({...auto}); setShowDialog(true); }}>
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button size="sm" variant="ghost" className="text-red-500" onClick={() => handleDelete(auto.automezzo_id, auto.targa)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Dialog Nuovo/Modifica Automezzo */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{editingId ? 'Modifica Automezzo' : 'Nuovo Automezzo'}</DialogTitle></DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Targa *</Label><Input value={formData.targa} onChange={(e) => setFormData({...formData, targa: e.target.value.toUpperCase()})} placeholder="AA000BB" data-testid="input-targa" /></div>
              <div><Label>Tipo Veicolo</Label>
                <Select value={formData.tipo_veicolo || 'altro'} onValueChange={(v) => setFormData({...formData, tipo_veicolo: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{TIPI_VEICOLO.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Marca</Label><Input value={formData.marca} onChange={(e) => setFormData({...formData, marca: e.target.value})} placeholder="Es: Fiat" /></div>
              <div><Label>Modello</Label><Input value={formData.modello} onChange={(e) => setFormData({...formData, modello: e.target.value})} placeholder="Es: Ducato" /></div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div><Label>Anno</Label><Input type="number" value={formData.anno_immatricolazione || ''} onChange={(e) => setFormData({...formData, anno_immatricolazione: e.target.value})} placeholder="2020" /></div>
              <div><Label>Alimentazione</Label>
                <Select value={formData.alimentazione || 'diesel'} onValueChange={(v) => setFormData({...formData, alimentazione: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{ALIMENTAZIONI.map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Km Attuali</Label><Input type="number" value={formData.km_attuali || ''} onChange={(e) => setFormData({...formData, km_attuali: e.target.value})} placeholder="0" /></div>
            </div>
            <div><Label>Note</Label><Input value={formData.note || ''} onChange={(e) => setFormData({...formData, note: e.target.value})} placeholder="Note opzionali" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Annulla</Button>
            <Button onClick={handleSave} disabled={submitting}>{submitting ? 'Salvataggio...' : 'Salva'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Dettaglio Automezzo */}
      <Dialog open={showDettaglio} onOpenChange={setShowDettaglio}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          {dettaglio && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Car className="w-5 h-5" /> {dettaglio.automezzo.targa} — {dettaglio.automezzo.marca} {dettaglio.automezzo.modello}
                </DialogTitle>
              </DialogHeader>
              <Tabs defaultValue="assicurazioni" className="mt-4">
                <TabsList className="grid grid-cols-3 w-full">
                  <TabsTrigger value="assicurazioni"><Shield className="w-4 h-4 mr-1" /> Assicurazioni</TabsTrigger>
                  <TabsTrigger value="revisioni"><ClipboardCheck className="w-4 h-4 mr-1" /> Revisioni</TabsTrigger>
                  <TabsTrigger value="manutenzioni"><Wrench className="w-4 h-4 mr-1" /> Manutenzioni</TabsTrigger>
                </TabsList>

                <TabsContent value="assicurazioni" className="space-y-3">
                  <Button size="sm" onClick={() => { setSubFormData({compagnia:'', numero_polizza:'', data_inizio:'', data_scadenza:'', tipo_copertura:'RCA', frazionamento:'annuale', premio_annuo:'', premio_rata:'', note:''}); setShowAssicForm(true); }}>
                    <Plus className="w-4 h-4 mr-1" /> Aggiungi Polizza
                  </Button>
                  {dettaglio.assicurazioni.length === 0 ? <p className="text-sm text-slate-500 p-4">Nessuna polizza registrata</p> :
                    <Table><TableHeader><TableRow><TableHead>Compagnia</TableHead><TableHead>Polizza</TableHead><TableHead>Tipo</TableHead><TableHead>Frazionamento</TableHead><TableHead>Scadenza</TableHead><TableHead>Premio</TableHead></TableRow></TableHeader>
                    <TableBody>{dettaglio.assicurazioni.map((a,i) => (
                      <TableRow key={i}><TableCell className="font-semibold">{a.compagnia}</TableCell><TableCell>{a.numero_polizza}</TableCell><TableCell>{a.tipo_copertura}</TableCell>
                      <TableCell><Badge variant="outline" className="text-xs">{a.frazionamento || 'annuale'}</Badge></TableCell>
                      <TableCell>{isScaduto(a.data_scadenza) ? <Badge className="bg-red-100 text-red-700">{formatDate(a.data_scadenza)}</Badge> : formatDate(a.data_scadenza)}</TableCell>
                      <TableCell>{a.premio_annuo ? `${a.premio_annuo} EUR` : '-'}{a.premio_rata ? <span className="text-xs text-slate-500 block">Rata: {a.premio_rata} EUR</span> : ''}</TableCell></TableRow>
                    ))}</TableBody></Table>}
                </TabsContent>

                <TabsContent value="revisioni" className="space-y-3">
                  <Button size="sm" onClick={() => { setSubFormData({data_revisione:'', esito:'superata', km_revisione:'', officina:'', prossima_scadenza:'', note:''}); setShowRevForm(true); }}>
                    <Plus className="w-4 h-4 mr-1" /> Aggiungi Revisione
                  </Button>
                  {dettaglio.revisioni.length === 0 ? <p className="text-sm text-slate-500 p-4">Nessuna revisione registrata</p> :
                    <Table><TableHeader><TableRow><TableHead>Data</TableHead><TableHead>Esito</TableHead><TableHead>Km</TableHead><TableHead>Officina</TableHead><TableHead>Prossima</TableHead></TableRow></TableHeader>
                    <TableBody>{dettaglio.revisioni.map((r,i) => (
                      <TableRow key={i}><TableCell>{formatDate(r.data_revisione)}</TableCell>
                      <TableCell><Badge className={r.esito === 'superata' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>{r.esito}</Badge></TableCell>
                      <TableCell>{r.km_revisione || '-'}</TableCell><TableCell>{r.officina || '-'}</TableCell>
                      <TableCell>{r.prossima_scadenza ? formatDate(r.prossima_scadenza) : '-'}</TableCell></TableRow>
                    ))}</TableBody></Table>}
                </TabsContent>

                <TabsContent value="manutenzioni" className="space-y-3">
                  <Button size="sm" onClick={() => { setSubFormData({data_intervento:'', tipo_intervento:'Ordinaria', descrizione:'', km_intervento:'', officina:'', costo:'', note:''}); setShowManutForm(true); }}>
                    <Plus className="w-4 h-4 mr-1" /> Aggiungi Manutenzione
                  </Button>
                  {dettaglio.manutenzioni.length === 0 ? <p className="text-sm text-slate-500 p-4">Nessuna manutenzione registrata</p> :
                    <Table><TableHeader><TableRow><TableHead>Data</TableHead><TableHead>Tipo</TableHead><TableHead>Descrizione</TableHead><TableHead>Km</TableHead><TableHead>Costo</TableHead></TableRow></TableHeader>
                    <TableBody>{dettaglio.manutenzioni.map((m,i) => (
                      <TableRow key={i}><TableCell>{formatDate(m.data_intervento)}</TableCell><TableCell><Badge variant="outline">{m.tipo_intervento}</Badge></TableCell>
                      <TableCell>{m.descrizione}</TableCell><TableCell>{m.km_intervento || '-'}</TableCell>
                      <TableCell>{m.costo ? `${m.costo} EUR` : '-'}</TableCell></TableRow>
                    ))}</TableBody></Table>}
                </TabsContent>
              </Tabs>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Sub-form Assicurazione */}
      <Dialog open={showAssicForm} onOpenChange={setShowAssicForm}>
        <DialogContent className="max-w-lg"><DialogHeader><DialogTitle>Nuova Polizza Assicurativa</DialogTitle></DialogHeader>
          <div className="grid gap-3 py-4">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Compagnia *</Label><Input value={subFormData.compagnia||''} onChange={(e) => setSubFormData({...subFormData, compagnia: e.target.value})} /></div>
              <div><Label>N. Polizza</Label><Input value={subFormData.numero_polizza||''} onChange={(e) => setSubFormData({...subFormData, numero_polizza: e.target.value})} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Data Inizio *</Label><Input type="date" value={subFormData.data_inizio||''} onChange={(e) => setSubFormData({...subFormData, data_inizio: e.target.value})} /></div>
              <div><Label>Data Scadenza *</Label><Input type="date" value={subFormData.data_scadenza||''} onChange={(e) => setSubFormData({...subFormData, data_scadenza: e.target.value})} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Tipo Copertura</Label><Input value={subFormData.tipo_copertura||'RCA'} onChange={(e) => setSubFormData({...subFormData, tipo_copertura: e.target.value})} placeholder="RCA, Kasko, ecc." /></div>
              <div><Label>Frazionamento</Label>
                <Select value={subFormData.frazionamento||'annuale'} onValueChange={(v) => setSubFormData({...subFormData, frazionamento: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="annuale">Annuale</SelectItem>
                    <SelectItem value="semestrale">Semestrale</SelectItem>
                    <SelectItem value="quadrimestrale">Quadrimestrale</SelectItem>
                    <SelectItem value="trimestrale">Trimestrale</SelectItem>
                    <SelectItem value="temporanea">Temporanea</SelectItem>
                    <SelectItem value="premio_unico">Premio Unico</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Premio Annuo (EUR)</Label><Input type="number" step="0.01" value={subFormData.premio_annuo||''} onChange={(e) => setSubFormData({...subFormData, premio_annuo: parseFloat(e.target.value) || null})} /></div>
              {subFormData.frazionamento && !['annuale','temporanea','premio_unico'].includes(subFormData.frazionamento) && (
                <div><Label>Premio Rata (EUR)</Label><Input type="number" step="0.01" value={subFormData.premio_rata||''} onChange={(e) => setSubFormData({...subFormData, premio_rata: parseFloat(e.target.value) || null})} /></div>
              )}
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setShowAssicForm(false)}>Annulla</Button><Button onClick={() => handleSubFormSave('assicurazione')}>Salva</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Sub-form Revisione */}
      <Dialog open={showRevForm} onOpenChange={setShowRevForm}>
        <DialogContent><DialogHeader><DialogTitle>Nuova Revisione</DialogTitle></DialogHeader>
          <div className="grid gap-3 py-4">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Data Revisione *</Label><Input type="date" value={subFormData.data_revisione||''} onChange={(e) => setSubFormData({...subFormData, data_revisione: e.target.value})} /></div>
              <div><Label>Esito</Label>
                <Select value={subFormData.esito||'superata'} onValueChange={(v) => setSubFormData({...subFormData, esito: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="superata">Superata</SelectItem><SelectItem value="non_superata">Non Superata</SelectItem><SelectItem value="da_ripetere">Da Ripetere</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Km Revisione</Label><Input type="number" value={subFormData.km_revisione||''} onChange={(e) => setSubFormData({...subFormData, km_revisione: parseInt(e.target.value)||null})} /></div>
              <div><Label>Officina</Label><Input value={subFormData.officina||''} onChange={(e) => setSubFormData({...subFormData, officina: e.target.value})} /></div>
            </div>
            <div><Label>Prossima Scadenza</Label><Input type="date" value={subFormData.prossima_scadenza||''} onChange={(e) => setSubFormData({...subFormData, prossima_scadenza: e.target.value})} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setShowRevForm(false)}>Annulla</Button><Button onClick={() => handleSubFormSave('revisione')}>Salva</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Sub-form Manutenzione */}
      <Dialog open={showManutForm} onOpenChange={setShowManutForm}>
        <DialogContent><DialogHeader><DialogTitle>Nuova Manutenzione</DialogTitle></DialogHeader>
          <div className="grid gap-3 py-4">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Data Intervento *</Label><Input type="date" value={subFormData.data_intervento||''} onChange={(e) => setSubFormData({...subFormData, data_intervento: e.target.value})} /></div>
              <div><Label>Tipo Intervento</Label><Input value={subFormData.tipo_intervento||''} onChange={(e) => setSubFormData({...subFormData, tipo_intervento: e.target.value})} placeholder="Ordinaria, Tagliando, Gomme..." /></div>
            </div>
            <div><Label>Descrizione *</Label><Input value={subFormData.descrizione||''} onChange={(e) => setSubFormData({...subFormData, descrizione: e.target.value})} placeholder="Descrizione intervento" /></div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label>Km</Label><Input type="number" value={subFormData.km_intervento||''} onChange={(e) => setSubFormData({...subFormData, km_intervento: parseInt(e.target.value)||null})} /></div>
              <div><Label>Officina</Label><Input value={subFormData.officina||''} onChange={(e) => setSubFormData({...subFormData, officina: e.target.value})} /></div>
              <div><Label>Costo (EUR)</Label><Input type="number" value={subFormData.costo||''} onChange={(e) => setSubFormData({...subFormData, costo: parseFloat(e.target.value)||null})} /></div>
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setShowManutForm(false)}>Annulla</Button><Button onClick={() => handleSubFormSave('manutenzione')}>Salva</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
